import {
  useState, useEffect, useCallback, useRef, useContext,
} from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import PuzzleEngineV2 from '../components/puzzle/PuzzleEngineV2';
import PuzzleProgressBar from '../components/puzzle/PuzzleProgressBar';
import ResultsModal from '../components/puzzle/ResultsModal';
import PauseOverlay from '../components/puzzle/PauseOverlay';
import SettingsModal from '../components/SettingsModal';
import {
  getDailyPuzzle, getDailyProgress, saveDailyProgress, sendHeartbeat,
} from '../services/dailyPuzzleApi';
import { useSettings } from '../context/SettingsContext';
import { AuthContext } from '../context/AuthContext';
import { useGuestId } from '../hooks/useGuestId';
import { formatTime, getLocalDateString } from '../utils/formatters';
import { getStreak, updateGuestStreak } from '../utils/streak';
import { getPublicStats } from '../services/statsApi';

// Stats bar component for the guest landing page
function StatsBar() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getPublicStats()
      .then(setStats)
      .catch(() => {});
  }, []);

  if (!stats) return null;

  const items = [
    { value: stats.totalCompletions, label: 'Puzzles Completed' },
    { value: stats.totalUsers, label: 'Players' },
    { value: stats.totalPuzzles, label: 'Daily Puzzles' },
    { value: stats.bestStreak, label: 'Day Best Streak' },
  ];

  const formatNumber = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  return (
    <section className="px-4 py-10 bg-puzzle-background">
      <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
        {items.map((item) => (
          <div key={item.label}>
            <div className="text-3xl sm:text-4xl font-bold text-puzzle-blue mb-1">
              {formatNumber(item.value)}
            </div>
            <div className="text-sm text-puzzle-text-secondary font-medium">
              {item.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// localStorage keys
const DAILY_PROGRESS_KEY = 'dailyPuzzleProgress';
const DAILY_PUZZLE_KEY = 'dailyPuzzle';

// Get cached puzzle from localStorage (only if it's for today and has puzzleNumber)
const getCachedPuzzle = () => {
  try {
    const stored = localStorage.getItem(DAILY_PUZZLE_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    // Only return if cached puzzle is for today AND has puzzleNumber
    if (data.date === getLocalDateString() && data.puzzle?.puzzleNumber) {
      return data.puzzle;
    }
    // Clear stale or incomplete cache
    localStorage.removeItem(DAILY_PUZZLE_KEY);
    return null;
  } catch {
    return null;
  }
};

// Cache puzzle in localStorage
const cachePuzzle = (puzzleData) => {
  try {
    const data = {
      date: getLocalDateString(),
      puzzle: puzzleData,
    };
    localStorage.setItem(DAILY_PUZZLE_KEY, JSON.stringify(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to cache puzzle:', error);
  }
};

// Get guest progress from localStorage
const getLocalProgress = (puzzleDate) => {
  try {
    const stored = localStorage.getItem(DAILY_PROGRESS_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    // Only return if it's for today's puzzle
    if (data.date === puzzleDate) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
};

// Save guest progress to localStorage
const saveLocalProgress = (puzzleDate, progressData) => {
  try {
    const data = {
      date: puzzleDate,
      ...progressData,
    };
    localStorage.setItem(DAILY_PROGRESS_KEY, JSON.stringify(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save progress to localStorage:', error);
  }
};

function DailyPuzzle() {
  const { user, loading: authLoading, refreshUser } = useContext(AuthContext);
  const {
    soundEnabled,
    showTimer,
    showMoveCount,
    confettiEnabled,
    theme,
  } = useSettings();
  // Ensure guestId is generated for non-authenticated users
  useGuestId();
  const [puzzle, setPuzzle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moveCount, setMoveCount] = useState(0);
  const [percentComplete, setPercentComplete] = useState(0);
  const [showLanding, setShowLanding] = useState(true);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [isPaused, setIsPaused] = useState(false);
  const [rating, setRating] = useState(null);
  const [newBadges, setNewBadges] = useState([]);
  const [leaguePoints, setLeaguePoints] = useState(null);
  const [leagueTier, setLeagueTier] = useState(null);
  const [isSavingCompletion, setIsSavingCompletion] = useState(false);
  const saveTimeoutRef = useRef(null);
  const timerRef = useRef(null);
  const serverSyncIntervalRef = useRef(null);
  const statsRef = useRef({ elapsedTime: 0, moveCount: 0 });
  const canvasRef = useRef(null);
  const lastPreviewRef = useRef(null);
  const wasAlreadyCompleteRef = useRef(false);
  const hasFetchedRef = useRef(false); // Prevents timer from starting before fetch completes
  const hasInitialSyncRef = useRef(false); // Ensures first server sync fires quickly
  const completionBannerRef = useRef(null);
  const puzzleConfigRef = useRef(null);
  const puzzleIdRef = useRef(null);
  const puzzleDateRef = useRef(null);
  const puzzleContainerRef = useRef(null);
  const isCompleteRef = useRef(false);
  const showLandingRef = useRef(true);
  const [containerWidth, setContainerWidth] = useState(null);

  // Keep refs in sync with state (for use in cleanup functions)
  useEffect(() => { isCompleteRef.current = isComplete; }, [isComplete]);
  useEffect(() => { showLandingRef.current = showLanding; }, [showLanding]);

  // Measure container width on mount (once, for responsive canvas sizing)
  useEffect(() => {
    if (puzzleContainerRef.current) {
      setContainerWidth(puzzleContainerRef.current.offsetWidth);
    }
  }, [showLanding]); // Re-measure when transitioning from landing to puzzle view

  // Format today's date for landing page
  const formatDateLanding = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format today's date for puzzle header
  const formatDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  useEffect(() => {
    // Wait for auth to finish loading before fetching puzzle
    // This prevents double-fetch when user state changes from null to user object
    if (authLoading) return;

    const fetchDailyPuzzle = async () => {
      try {
        setIsLoading(true);

        // Try cached puzzle first (for both guests and logged-in users)
        let puzzleData = getCachedPuzzle();

        if (!puzzleData) {
          // Fetch from server and cache
          puzzleData = await getDailyPuzzle();
          cachePuzzle(puzzleData);
        } else if (user) {
          // Cached puzzle found — refresh progress from server for logged-in users
          // to detect completion or progress updates from other devices
          try {
            const freshProgress = await getDailyProgress();
            if (freshProgress.state) {
              puzzleData = {
                ...puzzleData,
                progress: {
                  state: freshProgress.state,
                  stats: freshProgress.stats,
                },
              };
            }
          } catch {
            // Silently fall through to cached progress
          }
        }

        setPuzzle(puzzleData);
        // eslint-disable-next-line no-underscore-dangle
        puzzleIdRef.current = puzzleData._id;
        puzzleConfigRef.current = puzzleData.config;
        puzzleDateRef.current = puzzleData.date;

        // Load progress from localStorage (same for guests and logged-in)
        const localProgress = getLocalProgress(puzzleData.date);
        const serverComplete = puzzleData.progress?.state?.isComplete || false;

        // Re-sync: if localStorage says complete but server doesn't,
        // the original completion save must have failed — retry now.
        // AuthContext has already validated the token, so this uses a fresh session.
        // If it fails again, it'll retry on the next page load.
        if (localProgress?.isComplete && !serverComplete) {
          try {
            await saveDailyProgress({
              isComplete: true,
              playTime: localProgress.playTime || 0,
              moves: localProgress.moves || 0,
              piecePositions: localProgress.piecePositions,
              canvasWidth: localProgress.canvasWidth,
              deviceTier: localProgress.deviceTier,
            });
          } catch {
            // Best effort — will retry next page load
          }
        }

        // Use localStorage ONLY if it agrees with server about completion,
        // or if the server doesn't say it's complete.
        // When server says complete but localStorage doesn't (completed on another device),
        // skip stale localStorage and use server data instead (Path B).
        const useLocalProgress = localProgress
          && (localProgress.isComplete || !serverComplete);

        if (useLocalProgress) {
          const wasComplete = localProgress.isComplete || serverComplete;
          setIsComplete(wasComplete);
          wasAlreadyCompleteRef.current = wasComplete;

          // Restore saved stats
          const savedTime = localProgress.playTime || 0;
          const savedMoves = localProgress.moves || 0;
          setElapsedTime(savedTime);
          setMoveCount(savedMoves);
          statsRef.current = { elapsedTime: savedTime, moveCount: savedMoves };

          // Calculate initial percentage from saved state
          if (localProgress.piecePositions && localProgress.piecePositions.length > 0) {
            let connections = 0;
            localProgress.piecePositions.forEach((piece) => {
              if (piece.rightConnectionId) connections += 1;
              if (piece.downConnectionId) connections += 1;
            });
            const { rows, columns } = puzzleData.config;
            const maxConnections = (rows * (columns - 1)) + ((rows - 1) * columns);
            if (maxConnections > 0) {
              setPercentComplete(Math.round((connections / maxConnections) * 100));
            }
          }

          // Store saved state on puzzle for PuzzleCanvas
          setPuzzle((prev) => ({
            ...prev,
            savedState: {
              pieces: localProgress.piecePositions,
              deviceTier: localProgress.deviceTier,
              isComplete: wasComplete, // Use corrected value (may come from server)
              canvasWidth: localProgress.canvasWidth,
            },
          }));

          // Skip landing page for completed puzzles - show completed puzzle directly
          if (wasComplete) {
            setShowLanding(false);
          }
        } else if (puzzleData.progress?.state) {
          // Server returned progress (completed OR in-progress from another device/session)
          const serverProgress = puzzleData.progress;
          const wasComplete = serverProgress.state.isComplete || false;
          setIsComplete(wasComplete);
          wasAlreadyCompleteRef.current = wasComplete;

          // Restore stats from server
          if (serverProgress.stats) {
            const savedTime = serverProgress.stats.totalPlayTime || 0;
            const savedMoves = serverProgress.stats.moveCount || 0;
            setElapsedTime(savedTime);
            setMoveCount(savedMoves);
            statsRef.current = { elapsedTime: savedTime, moveCount: savedMoves };
          }

          // Calculate percentage from piece connections (or 100 if complete)
          if (wasComplete) {
            setPercentComplete(100);
          } else if (serverProgress.state.pieces && serverProgress.state.pieces.length > 0) {
            let connections = 0;
            serverProgress.state.pieces.forEach((piece) => {
              if (piece.rightConnectionId) connections += 1;
              if (piece.downConnectionId) connections += 1;
            });
            const { rows, columns } = puzzleData.config;
            const maxConnections = (rows * (columns - 1)) + ((rows - 1) * columns);
            if (maxConnections > 0) {
              setPercentComplete(Math.round((connections / maxConnections) * 100));
            }
          }

          // Restore saved state so PuzzleCanvas shows puzzle with progress
          setPuzzle((prev) => ({
            ...prev,
            savedState: {
              pieces: serverProgress.state.pieces,
              canvasWidth: serverProgress.state.canvasWidth,
              deviceTier: serverProgress.state.deviceTier,
              isComplete: wasComplete,
            },
          }));

          // Skip landing page when we have progress to restore (completed or in-progress)
          // Without this, users on a new device see "Start Puzzle" and think progress is lost
          if (wasComplete
            || (serverProgress.state.pieces && serverProgress.state.pieces.length > 0)) {
            setShowLanding(false);
          }
        } else {
          // No progress from localStorage or server — fresh puzzle
          // This handles the case where a user logs out after completing (React state persists)
          setIsComplete(false);
          wasAlreadyCompleteRef.current = false;
          setElapsedTime(0);
          setMoveCount(0);
          setPercentComplete(0);
          statsRef.current = { elapsedTime: 0, moveCount: 0 };
        }

        setError(null);

        // Initialize streak from user or localStorage
        setStreak(getStreak(user));

        // Mark fetch as complete - timer effect waits for this
        hasFetchedRef.current = true;
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDailyPuzzle();
  }, [user, authLoading]);

  // Sync progress to server (for persistence across logout/devices)
  const syncProgressToServer = useCallback(async () => {
    // Only sync if puzzle is in progress (not complete, not on landing)
    if (!puzzleDateRef.current || isCompleteRef.current || showLandingRef.current) return;

    const localProgress = getLocalProgress(puzzleDateRef.current);
    if (!localProgress || !localProgress.piecePositions) return;

    try {
      await saveDailyProgress({
        piecePositions: localProgress.piecePositions,
        canvasWidth: localProgress.canvasWidth,
        playTime: statsRef.current.elapsedTime,
        moves: statsRef.current.moveCount,
        deviceTier: localProgress.deviceTier,
      });
    } catch (err) {
      // Silently fail - server sync is non-critical during play
    }
  }, []);

  // Auto-pause and sync to server when tab becomes hidden or window loses focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !isComplete && puzzle && !showLanding) {
        setIsPaused(true);
        // Sync progress to server when leaving tab
        syncProgressToServer();
      }
    };

    // Blur catches macOS desktop/space switches (visibilitychange doesn't fire for those)
    const handleBlur = () => {
      if (!isComplete && puzzle && !showLanding) {
        setIsPaused(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [isComplete, puzzle, showLanding, syncProgressToServer]);

  // Heartbeat every 5 seconds (only when not paused and puzzle is active)
  // Syncs displayed time with server-tracked time for anti-cheat
  useEffect(() => {
    if (!puzzle || isComplete || isPaused || showLanding) return undefined;

    // Helper to sync time from server response
    const syncServerTime = (response) => {
      if (response?.success && response.serverTime !== undefined) {
        setElapsedTime(response.serverTime);
        statsRef.current.elapsedTime = response.serverTime;
        // Also update localStorage so refresh shows correct time
        const existingProgress = getLocalProgress(puzzleDateRef.current);
        if (existingProgress) {
          saveLocalProgress(puzzleDateRef.current, {
            ...existingProgress,
            playTime: response.serverTime,
          });
        }
      }
    };

    const heartbeatInterval = setInterval(async () => {
      try {
        const response = await sendHeartbeat();
        syncServerTime(response);
      } catch (err) {
        // Silently fail - heartbeat is non-critical
      }
    }, 5000);

    // Send initial heartbeat when starting/resuming (resume=true to reset timestamp)
    sendHeartbeat(true).then(syncServerTime).catch(() => {});

    return () => clearInterval(heartbeatInterval);
  }, [puzzle, isComplete, isPaused, showLanding]);

  // Timer effect (respects pause state)
  // Use hasFetchedRef to ensure timer doesn't start until fetchDailyPuzzle completes
  // Use wasAlreadyCompleteRef to guard against race condition with async state updates
  useEffect(() => {
    const canStartTimer = puzzle && !isComplete && !isPaused && !showLanding
      && hasFetchedRef.current && !wasAlreadyCompleteRef.current;
    if (canStartTimer) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => {
          const newTime = prev + 1;
          statsRef.current.elapsedTime = newTime;
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [puzzle, isComplete, isPaused, showLanding]);

  // Server sync every 60 seconds (backup for progress persistence across logout/devices)
  useEffect(() => {
    if (!puzzle || isComplete || isPaused || showLanding) {
      // Clear interval if puzzle is not active
      if (serverSyncIntervalRef.current) {
        clearInterval(serverSyncIntervalRef.current);
        serverSyncIntervalRef.current = null;
      }
      return undefined;
    }

    // Periodic server sync as safety net (primary sync happens on each piece move)
    serverSyncIntervalRef.current = setInterval(() => {
      syncProgressToServer();
    }, 30000);

    return () => {
      if (serverSyncIntervalRef.current) {
        clearInterval(serverSyncIntervalRef.current);
        serverSyncIntervalRef.current = null;
      }
    };
  }, [puzzle, isComplete, isPaused, showLanding, syncProgressToServer]);

  // Calculate percentage complete
  const calculatePercentComplete = useCallback((piecePositions, puzzleRows, puzzleCols) => {
    if (!piecePositions || piecePositions.length === 0) return 0;
    let connections = 0;
    piecePositions.forEach((piece) => {
      if (piece.rightConnectionId) connections += 1;
      if (piece.downConnectionId) connections += 1;
    });
    const maxConnections = (puzzleRows * (puzzleCols - 1)) + ((puzzleRows - 1) * puzzleCols);
    if (maxConnections === 0) return 0;
    return Math.round((connections / maxConnections) * 100);
  }, []);

  // Handle move events
  const handleMove = useCallback(() => {
    setMoveCount((prev) => {
      const newCount = prev + 1;
      statsRef.current.moveCount = newCount;
      return newCount;
    });
  }, []);

  const handleProgress = useCallback((progressData) => {
    // Don't save progress updates after completion (prevents overwriting isComplete: true)
    if (isCompleteRef.current) return;

    if (progressData.previewImage) {
      lastPreviewRef.current = progressData.previewImage;
    }

    if (progressData.piecePositions?.pieces && puzzleConfigRef.current) {
      const pct = calculatePercentComplete(
        progressData.piecePositions.pieces,
        puzzleConfigRef.current.rows,
        puzzleConfigRef.current.columns,
      );
      setPercentComplete(pct);
    }

    // Debounce save to localStorage (for both guests and logged-in users)
    // Server sync only happens on completion for logged-in users
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const { deviceTier, pieces, canvasWidth: savedCanvasWidth } = progressData.piecePositions;

      // Save to localStorage for ALL users
      saveLocalProgress(puzzleDateRef.current, {
        piecePositions: pieces,
        isComplete: progressData.isComplete,
        playTime: statsRef.current.elapsedTime,
        moves: statsRef.current.moveCount,
        deviceTier,
        canvasWidth: savedCanvasWidth,
      });

      // Also sync to server for cross-device access
      syncProgressToServer();
    }, hasInitialSyncRef.current ? 2000 : 500);

    // Mark initial sync as done after first progress event
    if (!hasInitialSyncRef.current) {
      hasInitialSyncRef.current = true;
    }
  }, [calculatePercentComplete, syncProgressToServer]);

  // Handle rating selection
  const handleRatingSelect = useCallback(async (selectedRating) => {
    setRating(selectedRating);
    try {
      await saveDailyProgress({ rating: selectedRating });
    } catch (err) {
      // Silently fail - rating is non-critical
    }
  }, []);

  const handleComplete = useCallback(async (previewImage, finalPositions) => {
    // Don't re-run completion logic if puzzle was already complete
    // or if completion is already being processed (prevents duplicate API calls)
    if (wasAlreadyCompleteRef.current || isCompleteRef.current) {
      setShowResultsModal(true);
      return;
    }

    setIsComplete(true);
    isCompleteRef.current = true; // Sync ref immediately so handleProgress won't overwrite
    setIsSavingCompletion(true); // Prevent premature modal open via banner button

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Fire confetti if enabled
    if (confettiEnabled) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });

      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
        });
      }, 250);
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Update localStorage with completion status and final piece positions
    const existingProgress = getLocalProgress(puzzleDateRef.current) || {};
    saveLocalProgress(puzzleDateRef.current, {
      ...existingProgress,
      isComplete: true,
      playTime: statsRef.current.elapsedTime,
      moves: statsRef.current.moveCount,
      ...(finalPositions ? {
        piecePositions: finalPositions.pieces,
        deviceTier: finalPositions.deviceTier,
        canvasWidth: finalPositions.canvasWidth,
      } : {}),
    });

    // Sync completion to server and update streak
    try {
      // Get deviceTier from localStorage to send to server
      const localProgress = getLocalProgress(puzzleDateRef.current);

      if (user) {
        // Logged-in users: save full stats, streak returned in response
        const response = await saveDailyProgress({
          isComplete: true,
          playTime: statsRef.current.elapsedTime,
          moves: statsRef.current.moveCount,
          previewImage,
          deviceTier: finalPositions?.deviceTier || localProgress?.deviceTier,
          piecePositions: finalPositions?.pieces,
          canvasWidth: finalPositions?.canvasWidth,
        });
        // Use streak from response (eliminates need for refreshUser call)
        if (response.streak) {
          setStreak({
            current: response.streak.current,
            longest: response.streak.longest,
          });
        }
        // Check for newly unlocked badges
        if (response.newBadges && response.newBadges.length > 0) {
          setNewBadges(response.newBadges);
        }
        // Check for league points
        if (response.leaguePoints) {
          setLeaguePoints(response.leaguePoints);
          setLeagueTier(response.leagueTier);
        }
      } else {
        // Guests: save completion status and deviceTier
        await saveDailyProgress({
          isComplete: true,
          deviceTier: finalPositions?.deviceTier || localProgress?.deviceTier,
          piecePositions: finalPositions?.pieces,
          canvasWidth: finalPositions?.canvasWidth,
        });
        // Update guest streak in localStorage
        const updatedStreak = updateGuestStreak();
        setStreak({
          current: updatedStreak.current,
          longest: updatedStreak.longest,
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save completion to server:', err);
      if (!user) {
        // Still update guest streak even if server sync failed
        const updatedStreak = updateGuestStreak();
        setStreak({
          current: updatedStreak.current,
          longest: updatedStreak.longest,
        });
      } else {
        // Server may have saved streak before erroring — try to fetch updated user
        try {
          const refreshedUser = await refreshUser();
          if (refreshedUser?.streak) {
            setStreak({
              current: refreshedUser.streak.current || 0,
              longest: refreshedUser.streak.longest || 0,
            });
          }
        } catch {
          // Both calls failed — streak stays at initialization value
        }
      }
    } finally {
      setIsSavingCompletion(false);
    }

    // Show modal AFTER streak is updated from server
    setShowResultsModal(true);
  }, [user, confettiEnabled, refreshUser]);

  // Cleanup on unmount - sync to server when navigating away
  useEffect(() => () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    if (serverSyncIntervalRef.current) {
      clearInterval(serverSyncIntervalRef.current);
    }
    // Sync progress to server when navigating away (route change)
    // This catches: puzzle page → /account → logout scenario
    if (!isCompleteRef.current && !showLandingRef.current && puzzleDateRef.current) {
      const localProgress = getLocalProgress(puzzleDateRef.current);
      if (localProgress?.piecePositions) {
        // Fire-and-forget sync (can't await in cleanup)
        saveDailyProgress({
          piecePositions: localProgress.piecePositions,
          canvasWidth: localProgress.canvasWidth,
          playTime: statsRef.current.elapsedTime,
          moves: statsRef.current.moveCount,
          deviceTier: localProgress.deviceTier,
        }).catch(() => {});
      }
    }
  }, []);

  // Guest creative landing page — show before loading/error checks so it always renders
  if (showLanding && !user) {
    return (
      <div className="min-h-screen bg-puzzle-surface">
        {/* Hero Section */}
        <section className="flex flex-col items-center justify-center px-4 pt-12 pb-16 sm:pt-20 sm:pb-24">
          <img
            src={theme === 'dark' ? '/images/logo_with_text_dark_mode.png' : '/images/logo_with_text.png'}
            alt="Puzzlists"
            className="h-36 sm:h-48 mb-6"
          />
          <h1 className="text-3xl sm:text-5xl font-bold text-puzzle-text text-center mb-4 max-w-2xl">
            Daily jigsaw puzzles for everyone
          </h1>
          <p className="text-lg sm:text-xl text-puzzle-text-secondary text-center mb-10 max-w-xl">
            Challenge yourself, track your progress, and compete with friends.
            No downloads &mdash; just open your browser and play.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={() => setShowLanding(false)}
              type="button"
              className="px-8 py-3 bg-puzzle-blue text-white font-semibold rounded-full hover:bg-blue-600 transition-colors text-lg"
            >
              Play Now
            </button>
            <Link
              to="/register"
              className="px-8 py-3 border-2 border-puzzle-blue text-puzzle-blue font-semibold rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-lg text-center"
            >
              Sign Up Free
            </Link>
          </div>
          <p className="text-sm text-puzzle-text-secondary">
            Already have an account?
            {' '}
            <Link to="/login" className="text-puzzle-blue hover:underline font-medium">
              Log in
            </Link>
          </p>
        </section>

        {/* Stats Bar */}
        <StatsBar />

        {/* Feature Cards */}
        <section className="px-4 py-16 sm:py-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-puzzle-text text-center mb-12">
              Everything you need to puzzle
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: '📅', title: 'Daily Puzzles', desc: 'A brand new puzzle every day at midnight. Build your streak and see how far you can go!' },
                { icon: '🏆', title: 'Leagues', desc: 'Compete in ranked divisions from Bronze to Diamond. Top players get promoted every two weeks.' },
                { icon: '👥', title: 'Friends', desc: 'Find players, send friend requests, and check out their profiles and stats.' },
                { icon: '🎯', title: 'Stats & Badges', desc: 'Track your lifetime stats — completions, play time, moves — and earn achievement badges.' },
                { icon: '🧩', title: 'Puzzle Rooms', desc: 'Create custom puzzles from any image and invite friends to compete on a private leaderboard.' },
                { icon: '🌙', title: 'Works Everywhere', desc: 'Play on desktop, tablet, or phone. Dark mode included. Your progress syncs across devices.' },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="bg-puzzle-background rounded-xl p-6 border border-puzzle-border hover:shadow-md transition-shadow"
                >
                  <div className="text-4xl mb-3">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-puzzle-text mb-2">{feature.title}</h3>
                  <p className="text-sm text-puzzle-text-secondary leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="px-4 py-16 sm:py-20 bg-puzzle-background">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-puzzle-text text-center mb-12">
              How it works
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {[
                { step: '1', title: 'Open today\'s puzzle', desc: 'A new puzzle drops every day at midnight. Just hit Play.' },
                { step: '2', title: 'Drag pieces to solve', desc: 'Pieces snap together automatically when you place them correctly.' },
                { step: '3', title: 'Track your progress', desc: 'Build streaks, earn badges, and climb the leagues.' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 bg-puzzle-blue text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-puzzle-text mb-2">{item.title}</h3>
                  <p className="text-sm text-puzzle-text-secondary">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 py-16 sm:py-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-puzzle-text mb-4">
            Ready to start puzzling?
          </h2>
          <p className="text-puzzle-text-secondary mb-8 max-w-md mx-auto">
            No account required to play &mdash; sign up to save your progress across devices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowLanding(false)}
              type="button"
              className="px-8 py-3 bg-puzzle-blue text-white font-semibold rounded-full hover:bg-blue-600 transition-colors text-lg"
            >
              Play Now
            </button>
            <Link
              to="/register"
              className="px-8 py-3 border-2 border-puzzle-blue text-puzzle-blue font-semibold rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-lg text-center"
            >
              Create Free Account
            </Link>
          </div>
        </section>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
        <p className="text-puzzle-text-secondary">Loading today&apos;s puzzle...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-puzzle-warning/30 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-puzzle-warning mb-2">No Daily Puzzle Today</h2>
          <p className="text-puzzle-warning/80">
            No puzzle has been scheduled for today. Check back tomorrow!
          </p>
        </div>
      </div>
    );
  }

  if (!puzzle) {
    return null;
  }

  const { rows, columns } = puzzle.config;
  const pieceCount = rows * columns;

  // Landing Page View — logged-in users get minimal landing
  if (showLanding && user) {
    return (
      <div className="min-h-screen bg-puzzle-surface flex flex-col items-center justify-center px-4">
        <img
          src={theme === 'dark' ? '/images/logo_with_text_dark_mode.png' : '/images/logo_with_text.png'}
          alt="Puzzlists"
          className="h-48 mb-6"
        />
        <p className="text-xl sm:text-2xl text-puzzle-text text-center mb-10">
          Complete today&apos;s daily puzzle.
        </p>
        <div className="flex gap-4 mb-12">
          <button
            onClick={() => setShowLanding(false)}
            type="button"
            className="px-8 py-3 bg-puzzle-blue text-white font-medium rounded-full hover:bg-blue-600 transition-colors"
          >
            Play
          </button>
        </div>
        <div className="text-center text-puzzle-text">
          <p className="font-medium">{formatDateLanding()}</p>
          <p className="text-sm text-puzzle-text-secondary">
            No.
            {' '}
            {puzzle.puzzleNumber}
          </p>
        </div>
      </div>
    );
  }

  // Puzzle View
  return (
    <div ref={puzzleContainerRef} className="w-full max-w-7xl mx-auto px-2 sm:px-4 pt-16 sm:pt-20 pb-4 sm:pb-8">
      {/* Results Modal */}
      {showResultsModal && (
        <ResultsModal
          elapsedTime={elapsedTime}
          moveCount={moveCount}
          pieceCount={pieceCount}
          puzzleNumber={puzzle.puzzleNumber}
          puzzleTitle={puzzle.title}
          puzzleDescription={puzzle.description}
          onClose={() => setShowResultsModal(false)}
          currentStreak={streak.current}
          longestStreak={streak.longest}
          rating={rating}
          onRatingSelect={handleRatingSelect}
          newBadges={newBadges}
          leaguePoints={leaguePoints}
          leagueTier={leagueTier}
          isGuest={!user}
        />
      )}

      {/* Floating Stats Bar - always visible during active play */}
      {!isPaused && !isComplete && (
        <div
          className="fixed top-28 left-1/2 -translate-x-1/2 z-40
                     bg-puzzle-surface/95 backdrop-blur-md
                     rounded-full shadow-lg border border-puzzle-border
                     px-4 py-2 flex items-center gap-3 sm:gap-4"
        >
          {/* Puzzle Number */}
          <span className="font-bold text-puzzle-text">
            #
            {puzzle.puzzleNumber}
          </span>

          {/* Divider */}
          <div className="w-px h-5 bg-puzzle-border" />

          {/* Timer */}
            {showTimer && (
              <span className="flex items-center gap-1 text-sm text-puzzle-text-secondary">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-puzzle-text">{formatTime(elapsedTime)}</span>
              </span>
            )}

            {/* Moves */}
            {showMoveCount && (
              <span className="flex items-center gap-1 text-sm text-puzzle-text-secondary">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
                <span className="font-medium text-puzzle-text">{moveCount}</span>
              </span>
            )}

          {/* Progress */}
          <span className="flex items-center gap-1 sm:gap-2 text-sm">
            <div className="w-12 sm:w-24">
              <PuzzleProgressBar progress={percentComplete} color="#2196F3" />
            </div>
            <span className="font-medium text-puzzle-text">
              {percentComplete}
              %
            </span>
          </span>

          {/* Divider */}
          <div className="w-px h-5 bg-puzzle-border" />

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            {/* Settings */}
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="p-1.5 text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover rounded-full transition-colors"
              title="Settings"
              aria-label="Open settings"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            {/* Shuffle */}
            <button
              type="button"
              onClick={() => canvasRef.current?.shuffle()}
              className="p-1.5 text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover rounded-full transition-colors"
              title="Shuffle pieces"
              aria-label="Shuffle pieces"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Pause */}
            <button
              type="button"
              onClick={() => setIsPaused(true)}
              className="p-1.5 text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover rounded-full transition-colors"
              title="Pause puzzle"
              aria-label="Pause puzzle"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-2 sm:mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-puzzle-text">Daily Puzzle</h1>
        <p className="text-sm text-puzzle-text-secondary">{formatDate()}</p>
        <p className="text-xs text-puzzle-text-secondary">
          {`${rows} x ${columns} (${pieceCount} pieces)`}
        </p>
      </div>

      {/* Completion Banner */}
      {isComplete && (
        <div
          ref={completionBannerRef}
          className="mb-6 bg-green-50 border border-puzzle-green/30 rounded-lg p-4"
        >
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg font-semibold text-puzzle-green">
              Daily Puzzle Complete!
            </span>
          </div>
          <p className="text-center text-puzzle-green/80 mt-2">
            {`Time: ${formatTime(elapsedTime)} | Moves: ${moveCount}`}
            {streak.current > 0 && ` | 🔥 ${streak.current} day${streak.current === 1 ? '' : 's'} streak`}
          </p>
          <div className="flex justify-center mt-3">
            <button
              type="button"
              onClick={() => setShowResultsModal(true)}
              disabled={isSavingCompletion}
              className={`px-4 py-2 text-white text-sm font-medium rounded-full transition-colors ${
                isSavingCompletion
                  ? 'bg-puzzle-green/50 cursor-not-allowed'
                  : 'bg-puzzle-green hover:bg-green-600'
              }`}
            >
              {isSavingCompletion ? 'Saving...' : 'Share Results'}
            </button>
          </div>
        </div>
      )}

      {/* Puzzle Canvas with Pause Overlay */}
      <div className="relative">
        {containerWidth && (
          <PuzzleEngineV2
            ref={canvasRef}
            imageData={puzzle.imageData}
            rows={puzzle.config.rows}
            columns={puzzle.config.columns}
            pieceStyle={puzzle.config.pieceStyle}
            isAlreadyComplete={wasAlreadyCompleteRef.current}
            savedPiecePositions={puzzle.savedState?.pieces}
            savedDeviceTier={puzzle.savedState?.deviceTier}
            savedCanvasWidth={puzzle.savedState?.canvasWidth}
            containerWidth={containerWidth}
            soundEnabled={soundEnabled}
            onComplete={handleComplete}
            onProgress={handleProgress}
            onMove={handleMove}
          />
        )}

        {/* Pause Overlay */}
        <AnimatePresence>
          {isPaused && !isComplete && (
            <PauseOverlay onResume={() => setIsPaused(false)} />
          )}
        </AnimatePresence>
      </div>

      {/* Guest Banner */}
      {!user && (
        <div className="mt-6 bg-blue-50 border border-puzzle-blue/30 rounded-lg p-4 text-center">
          <p className="text-sm text-puzzle-blue">
            {'Playing as guest. Progress is saved locally on this device. '}
            <Link to="/login" className="font-semibold underline hover:text-blue-600">
              Login
            </Link>
            {' or '}
            <Link to="/register" className="font-semibold underline hover:text-blue-600">
              Register
            </Link>
            {' to save progress across devices.'}
          </p>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

export default DailyPuzzle;
